import { Clock3, Images, Play } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useLook } from "@/context/LookContext";
import { useHub } from "@/components/AppShell";
import FamilyPhotos from "@/components/look/FamilyPhotos";

const IDLE = [1, 5, 10, 15, 30];
const MODES = [
  { id: "photos", label: "Photo slideshow + clock", hint: "Family photos cross-fade every 8 seconds.", Icon: Images },
  { id: "clock", label: "Clock + today", hint: "A big clock, the date and your day.", Icon: Clock3 },
];

export default function ScreensaverTab() {
  const { look, update, startScreensaver } = useLook();
  const hub = useHub()?.hub;
  const ss = look.screensaver;
  const set = (patch) => update({ screensaver: { ...ss, ...patch } });

  return (
    <div className="space-y-10" data-testid="screensaver-tab">
      <section className="q-card space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-display text-base font-bold text-q-text">Screensaver</p>
            <p className="text-sm text-q-muted" data-testid="screensaver-status">{ss.enabled ? `On · starts after ${ss.idle_minutes} min with no input` : "Off · it will never show"}</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-q-muted">{ss.enabled ? "On" : "Off"}</span>
            <Switch checked={ss.enabled} onCheckedChange={(v) => set({ enabled: v })} data-testid="screensaver-enabled-switch" aria-label="Screensaver on or off" />
          </div>
        </div>
        <div>
          <p className="q-label">Start after</p>
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Idle time">
            {IDLE.map((n) => (
              <button key={n} role="radio" aria-checked={ss.idle_minutes === n} disabled={!ss.enabled} onClick={() => set({ idle_minutes: n })} data-testid={`screensaver-idle-${n}`} data-active={ss.idle_minutes === n ? "1" : "0"}
                className={`q-btn px-4 py-2 ${ss.idle_minutes === n ? "q-btn-primary" : "q-btn-secondary"}`}>{n} min</button>
            ))}
          </div>
        </div>
        <div>
          <p className="q-label">Mode</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {MODES.map(({ id, label, hint, Icon }) => {
              const on = ss.mode === id;
              return (
                <button key={id} onClick={() => set({ mode: id })} disabled={!ss.enabled} data-testid={`screensaver-mode-${id}`} data-active={on ? "1" : "0"} aria-pressed={on}
                  className="flex items-start gap-3 rounded-xl border p-4 text-left transition-[border-color,box-shadow] duration-150 disabled:opacity-50"
                  style={{ borderColor: on ? "rgb(var(--q-accent))" : "rgb(var(--q-border))", boxShadow: on ? "0 0 0 1px rgb(var(--q-accent))" : "none", background: on ? "rgb(var(--q-accent) / 0.1)" : "rgb(var(--q-surface))" }}>
                  <span className="mt-0.5 inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg" style={{ background: "rgba(var(--glow-family),0.14)" }}>
                    <Icon className="h-4 w-4" style={{ color: "rgb(var(--glow-family))" }} />
                  </span>
                  <span><span className="block text-sm font-bold text-q-text">{label}</span><span className="block text-xs text-q-muted">{hint}</span></span>
                </button>
              );
            })}
          </div>
        </div>
        <button className="q-btn q-btn-primary" onClick={startScreensaver} disabled={!ss.enabled} data-testid="screensaver-try-btn"><Play className="h-4 w-4" /> Try it now</button>
        {!ss.enabled && <p className="text-xs text-q-muted">Turn the screensaver on to try it.</p>}
      </section>
      {hub ? <FamilyPhotos hub={hub} /> : (
        <p className="rounded-xl border border-dashed border-q-line px-5 py-8 text-center text-sm text-q-muted" data-testid="family-photos-no-hub">
          Family photos live in a family hub. Your slideshow shows your wallpaper and the clock.
        </p>
      )}
    </div>
  );
}
